import { FC, useCallback, useMemo } from "react";
import { Box, Step, StepButton, Stepper } from "@mui/material";
import { useWillUnmount } from "rooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { activeProviderConnectionCurrentStepSelector } from "@/store/provider-connections/selectors/active-connection/activeProviderConnectionCurrentStepSelector";
import {
  setActiveProviderConnectionActiveStep,
  setActiveProviderConnectionIsCommandGenerated,
  setActiveProviderConnectionIsCreated,
} from "@/store/provider-connections/providerConnectionsSlice";
import { SnowflakeConnectionDetails } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/details/SnowflakeConnectionDetails";
import { SnowflakeConnectionForm } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/form/SnowflakeConnectionForm";
import { SnowflakeConnectionCollectingData } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/collecting-data/SnowflakeConnectionCollectingData";
import { SnowflakeConnectionDrawerActions } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/SnowflakeConnectionDrawerActions";
import { activeProviderConnectionIsCommandGeneratedSelector } from "@/store/provider-connections/selectors/active-connection/activeProviderConnectionIsCommandGeneratedSelector";
import { activeProviderConnectionIsCreatedSelector } from "@/store/provider-connections/selectors/active-connection/activeProviderConnectionIsCreatedSelector";
import { generateSnowflakeConnectionSteps } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/form/utils/helpers/generateSnowflakeConnectionSteps";

interface SnowflakeConnectionDrawerProps {
  onClose: () => void;
}

export const SnowflakeConnectionDrawer: FC<SnowflakeConnectionDrawerProps> = ({
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const activeStep = useAppSelector(
    activeProviderConnectionCurrentStepSelector,
  );
  const isCommandGenerated = useAppSelector(
    activeProviderConnectionIsCommandGeneratedSelector,
  );
  const isConnectionCreated = useAppSelector(
    activeProviderConnectionIsCreatedSelector,
  );

  const steps = useMemo(() => {
    return generateSnowflakeConnectionSteps({
      activeStep,
      isCommandGenerated: isCommandGenerated ?? false,
      isConnectionCreated: isConnectionCreated ?? false,
    });
  }, [activeStep, isCommandGenerated, isConnectionCreated]);

  const stepChangeHandler = useCallback(
    (index: number) => {
      if (activeStep === 2) {
        return;
      }
      dispatch(setActiveProviderConnectionActiveStep(index));
    },
    [activeStep, dispatch],
  );

  useWillUnmount(() => {
    dispatch(setActiveProviderConnectionIsCommandGenerated(false));
    dispatch(setActiveProviderConnectionActiveStep(0));
    dispatch(setActiveProviderConnectionIsCreated(false));
  });

  return (
    <Box p={1.5} pb={3}>
      <Box width="100%">
        <Stepper nonLinear activeStep={activeStep} sx={stepperStyles}>
          {steps.map(({ label, index, completed, enabled }) => (
            <Step key={label} completed={completed && activeStep !== index}>
              <StepButton
                onClick={() => stepChangeHandler(index)}
                sx={{ borderRadius: 2 }}
                disabled={!enabled}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
      {activeStep === 0 && <SnowflakeConnectionDetails />}
      {activeStep === 1 && <SnowflakeConnectionForm />}
      {activeStep === 2 && <SnowflakeConnectionCollectingData />}
      {activeStep !== 2 && (
        <SnowflakeConnectionDrawerActions
          onCancel={onClose}
          onStepChange={stepChangeHandler}
          disabled={!steps[activeStep + 1].enabled}
        />
      )}
    </Box>
  );
};

const stepperStyles = {
  p: 3,
  bgcolor: "grey.100",
  borderRadius: 2,
};
